<template>
  <div
      :class="rootClasses"
      class="flex items-center">
    <div class="relative flex h-4 w-4 shrink-0 items-center justify-center">
      <input
          :id="name"
          :aria-checked="isChecked"
          :aria-disabled="isInactive"
          :aria-label="label || undefined"
          :aria-readonly="readonly || undefined"
          :checked="isChecked"
          :class="inputClasses"
          :data-bf-checkbox-state="visualState"
          :disabled="isInactive"
          :readonly="readonly"
          :tabindex="tabindex"
          class="bf-checkbox-input h-4 w-4 rounded focus:ring-2 focus:ring-blue-900/45"
          type="checkbox"
          @change="updateModelValue">
      <svg
          v-if="disabled"
          aria-hidden="true"
          class="pointer-events-none absolute h-3.5 w-3.5 text-white"
          data-testid="bf-checkbox-disabled-icon"
          viewBox="0 0 14 14">
        <path
            d="M4 4 10 10M10 4 4 10"
            fill="none"
            stroke="currentColor"
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="1.5"/>
      </svg>
      <svg
          v-else-if="isChecked"
          aria-hidden="true"
          class="pointer-events-none absolute h-3.5 w-3.5 text-white"
          data-testid="bf-checkbox-check-icon"
          viewBox="0 0 14 14">
        <path
            d="M11 3.5 5.75 10 3 7.25"
            fill="none"
            stroke="currentColor"
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="1.5"/>
      </svg>
      <svg
          v-else-if="indeterminate && !isChecked"
          aria-hidden="true"
          class="pointer-events-none absolute h-3.5 w-3.5 text-white"
          data-testid="bf-checkbox-indeterminate-icon"
          viewBox="0 0 14 14">
        <path
            d="M3.5 7h7"
            fill="none"
            stroke="currentColor"
            stroke-linecap="round"
            stroke-width="1.5"/>
      </svg>
    </div>
    <label v-if="label && $slots.default === undefined"
           :class="labelClasses"
           :for="name"
           class="ps-2 text-sm font-normal select-none">
      {{ label }}
    </label>
    <span v-else-if="$slots.default"
          :class="slotClasses"
          class="ps-2">
      <slot/>
    </span>
  </div>
</template>

<script lang="ts" setup>
type BfCheckboxValue = string | number | boolean | object;
type BfCheckboxModelValue = boolean | BfCheckboxValue[];

const props = defineProps({
  name: {
    type: String,
    required: false,
    default: null
  },
  label: {
    type: String,
    required: false,
    default: null
  },
  tabindex: {
    type: [Number, String],
    default: 0,
    required: false
  },
  modelValue: {
    type: [Array, Boolean] as PropType<BfCheckboxModelValue>,
    required: true
  },
  value: {
    type: [Number, String, Object, Boolean] as PropType<BfCheckboxValue>,
    required: false, // Not required for boolean usage
    default: undefined
  },
  disabled: {
    type: Boolean,
    default: false
  },
  readonly: {
    type: Boolean,
    default: false
  },
  indeterminate: {
    type: Boolean,
    default: false
  }
});

const emits = defineEmits(['update:modelValue']);

const hasCheckboxValue = (value: BfCheckboxValue | undefined): value is BfCheckboxValue => {
  return value !== undefined;
};

// Computed property to determine if the checkbox should be checked
const isChecked = computed(() => {
  if (Array.isArray(props.modelValue)) {
    return hasCheckboxValue(props.value) && props.modelValue.includes(props.value);
  }
  return Boolean(props.modelValue);
});

const isInactive = computed(() => props.disabled || props.readonly);

const visualState = computed(() => {
  if (props.disabled) {
    return 'disabled';
  }
  if (props.indeterminate && !isChecked.value){
    return 'indeterminate';
  }
  if (props.readonly && isChecked.value) {
    return 'readonly-checked';
  }
  if (props.readonly) {
    return 'readonly-unchecked';
  }
  if (isChecked.value) {
    return 'checked';
  }
  return 'default';
});

const updateModelValue = (event: Event) => {
  if (isInactive.value) {
    return;
  }

  const target = event.target as HTMLInputElement;

  if (Array.isArray(props.modelValue)) {
    if (!hasCheckboxValue(props.value)) {
      return;
    }

    const newValue = [...props.modelValue];
    if (target.checked) {
      if (!newValue.includes(props.value)) {
        newValue.push(props.value);
      }
    } else {
      const index = newValue.indexOf(props.value);
      if (index > -1) {
        newValue.splice(index, 1);
      }
    }
    emits('update:modelValue', newValue);
  } else {
    emits('update:modelValue', target.checked);
  }
};

const rootClasses = computed(() => {
  return {
    'cursor-pointer': !isInactive.value,
    'cursor-not-allowed': isInactive.value
  };
});

const inputClasses = computed(() => {
  return {
    'cursor-pointer': !isInactive.value,
    'cursor-not-allowed pointer-events-none': isInactive.value
  };
});

const labelClasses = computed(() => {
  return {
    'cursor-pointer text-text-secondary': !isInactive.value,
    'cursor-not-allowed pointer-events-none text-gray-400': isInactive.value
  };
});

const slotClasses = computed(() => {
  return {
    'pointer-events-none !text-gray-400': isInactive.value
  };
});
</script>

<style lang="scss" scoped>
.bf-checkbox-input {
  appearance: none;
  background-position: center;
  background-repeat: no-repeat;
  background-size: 0.75rem 0.75rem;
  @apply border border-black-100 bg-surface-white;

  &:focus {
    @apply border-blue-900;
  }

  &[data-bf-checkbox-state='default']:hover {
    @apply border-border-hover;
  }

  &[data-bf-checkbox-state='checked'] {
    @apply border-border-selected bg-border-selected;
  }

  &[data-bf-checkbox-state='readonly-checked'],
  &[data-bf-checkbox-state='disabled'] {
    @apply border-border-primary bg-border-primary;
  }

  &[data-bf-checkbox-state='readonly-unchecked'] {
    @apply border-black-100 bg-surface-white;
  }
  &[data-bf-checkbox-state='indeterminate'] {
    @apply border-border-selected bg-border-selected;
  }
}
</style>